// Skerpa context store — tracks what the user is doing right now
// Any page/component can push visible data here so the copilot sees it

import { create } from 'zustand'

export interface CanvasSectionState {
  totalSections: number
  populatedSections: number
  emptySections: string[]
}

interface SkerpaContextState {
  activeView: string
  viewTitle: string
  /** Structured snapshot of what's currently visible on screen */
  pageContext: string
  /** ID of the resource actively being edited (e.g., persona session ID) */
  activeResourceId: string | null
  /** Entity name to focus on (set by Skerpa, consumed by views to auto-navigate) */
  focusEntityName: string | null
  /** Canvas section fill state (set by CanvasExplorer) */
  canvasSectionState: CanvasSectionState | null
  /** A prompt that any caller (e.g. property pane "Ask Skerpa") wants the
   *  chat to send next. Consumed and cleared by the chat tab. */
  pendingPrompt: string | null
  /** Tick incremented every time someone requests the chat panel be opened.
   *  MainLayout watches this and toggles its local `isHermesChatOpen` state. */
  openChatTick: number
  /** Session ID that PersonaWorkshopPage should auto-open on next mount/update.
   *  Set by Skerpa after creating a persona, consumed by PersonaWorkshopPage. */
  pendingOpenSessionId: string | null
  setActiveView: (view: string, title: string) => void
  /** Pages call this to push their visible data for Skerpa */
  setPageContext: (context: string) => void
  /** Pages call this when user is editing a specific resource */
  setActiveResource: (id: string | null) => void
  /** Skerpa calls this to request a view focus on a named entity */
  setFocusEntity: (name: string | null) => void
  /** Canvas pages call this to report section fill state */
  setCanvasSectionState: (state: CanvasSectionState | null) => void
  /** Open the chat panel and (optionally) seed it with a prompt to send. */
  askSkerpa: (prompt?: string) => void
  /** Chat tab calls this after consuming `pendingPrompt`. */
  consumePendingPrompt: () => void
  /** Set or clear the session to auto-open in PersonaWorkshopPage. */
  setPendingOpenSession: (id: string | null) => void
}

export const useSkerpaContextStore = create<SkerpaContextState>((set) => ({
  activeView: 'workspace',
  viewTitle: 'Workspace',
  pageContext: '',
  activeResourceId: null,
  focusEntityName: null,
  canvasSectionState: null,
  pendingPrompt: null,
  openChatTick: 0,
  pendingOpenSessionId: null,
  setActiveView: (view, title) => set({ activeView: view, viewTitle: title, pageContext: '', activeResourceId: null, focusEntityName: null, canvasSectionState: null }),
  setPageContext: (context) => set({ pageContext: context }),
  setActiveResource: (id) => set({ activeResourceId: id }),
  setFocusEntity: (name) => set({ focusEntityName: name }),
  setCanvasSectionState: (state) => set({ canvasSectionState: state }),
  askSkerpa: (prompt) => set((s) => ({
    pendingPrompt: prompt ?? null,
    openChatTick: s.openChatTick + 1,
  })),
  consumePendingPrompt: () => set({ pendingPrompt: null }),
  setPendingOpenSession: (id) => set({ pendingOpenSessionId: id }),
}))
